import { Component, OnDestroy, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { Events } from '../../helpers/events';

// ionic
import { ModalController, PopoverController } from '@ionic/angular';
// import { PopoverController, Platform } from '@ionic/angular';

// other libraries
// import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject, Subscription } from "rxjs";
import { debounceTime, defaultIfEmpty, take, takeUntil } from "rxjs/operators";

// components
import { KeywordSelectComponent } from "../keyword-select/keyword-select.component";
import { PopoverSelectBoxComponent } from "../popover-select-box/popover-select-box.component";

// providers
import { NewsfeedService } from "../../services/newsfeed.service";
import { UserService } from "../../services/user.service";
import { EventService } from "../../services/event.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";
import { PaginatorService } from "../../services/paginator.service";
import { RouterExtService } from 'src/app/services/router-ext.service';

// models
import { Newsfeed } from "../../models/newsfeed";
import { User } from "../../models/user";
import { Event } from "../../models/event";
import { SelectBoxOption } from "../../models/select-box-option";

@Component({
    selector: 'newsfeed-list',
    templateUrl: './newsfeed-list.component.html',
    styleUrls: ['./newsfeed-list.component.scss']
})
export class NewsfeedListComponent implements OnInit, OnDestroy {

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * current event
     *
     * @type {User}
     */
    public user: User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = false;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public dataLoading: boolean = false;

    /**
     * list filter
     *
     * @type string
     */
    public filter: string;
    public filterKeywords: any[] = [];
    public filterCustomKeywords: any[] = [];
    public filterOptions: SelectBoxOption[] = [];

    public editId: number;

    /**
     * showing detail
     *
     * @type string
     */
    @Input() detail: boolean = false;

    /**
     * newsfeed
     *
     * @type {Newsfeed}
     */
    public items: Newsfeed[] = [];

    itemsTotal: number = 0;
    itemsLimit: number = 5;
    itemsOffset: number = 0;

    pager: any;
    page: number = 1;

    @Input() event: Event;
    @Input() showKeywords: boolean = true;

    @Output() onNewsfeedEdit: EventEmitter<Newsfeed> = new EventEmitter();

    // handlers for events
    keywordsChangeHandler;
    eventChangeHandler;
    refreshHandler;
    postUpdated;

    /**
     * constructor
     *
     * @param eventService
     * @param appEvents
     */
    constructor(
        private routerExtService: RouterExtService,
        public userService: UserService,
        public eventService: EventService,
        public translate: TranslateService,
        public appEvents: Events,
        public plt: PlatformService,
        public overlayService: OverlayService,
        public paginatorService: PaginatorService,
        public newsfeedService: NewsfeedService,
        public modalController: ModalController,
        public popoverController: PopoverController
    ) {
        this.userLang = this.translate.currentLang;
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {


        this.eventChangeHandler = () => {
            this.resetFilter();
            this.applyFilter();
        }

        this.keywordsChangeHandler = () => {
            this.resetFilter();
            this.applyFilter();
        }

        this.refreshHandler = () => {
            this.applyFilter();
        }


        // handler for procesing updates of specific post.. like/comments
        this.postUpdated = (updatedPost) => {
            this.items.forEach((post, i) => {
                if (updatedPost.id == post.id) {
                    this.items[i] = updatedPost
                }
            });
        }

        // connect events later to avoid duplicit load when switching event
        // setCurrentEvent is delayed
        setTimeout(() => {
            this.appEvents.subscribe('event:changed', this.eventChangeHandler);
            this.appEvents.subscribe('keywords:changed', this.keywordsChangeHandler);
            this.appEvents.subscribe('newsfeed:refresh', this.refreshHandler);
            this.appEvents.subscribe('newsfeed:post:udpated', this.postUpdated);
        }, 250);

        this.applyFilter();
    }

    /**
     * load more newsfeeds in infinitive scroll
     *
     * @param event
     *
     * @return void
     */
    public loadData(event) {
        this.refresh(event, false);
    }

    /**
     * init list
     */
    public refresh($event?, reset = true) {

        this.userService.getCurrentUser().pipe(
            take(1),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {

                this.user = user;
                // always check for current event
                this.eventService.getCurrentEvent().pipe(
                    take(1),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        if (event.id) {
                            this.event = event;
                            if (this.event.id && this.event.is_homepage_content_newsfeed) {

                                this.initFilterOptions();

                                if (!this.loading && !this.plt.disableBackgroundRefresh) {
                                    // avoid parallel loading
                                    this.loading = true;

                                    if (reset) {
                                        this.itemsOffset = 0;
                                        this.page = 1;
                                    }

                                    let subscription = this.newsfeedService.getListByEvent(
                                        this.event.id,
                                        this.itemsLimit,
                                        this.itemsOffset,
                                        this.filter,
                                        this.filterKeywords
                                    ).subscribe((response) => {
                                        if (reset) {
                                            this.items = [];
                                        }

                                        response['items'].forEach(item => {
                                            this.items.push(new Newsfeed(item));
                                        });
                                        this.itemsTotal = response['total'];

                                        // update offset by real count of items
                                        this.itemsOffset = this.items.length;

                                        this.pager = this.paginatorService.getPager(this.itemsTotal, this.page, this.itemsLimit);

                                        this.loading = false;
                                        this.dataLoading = false;

                                        // hide loading
                                        if ($event) {
                                            $event.target.complete();
                                        }
                                    },
                                        (error) => {
                                            console.info(error);
                                            this.loading = false;
                                            this.dataLoading = false;
                                            // hide loading
                                            if ($event) {
                                                $event.target.complete();
                                            }
                                        });

                                    this.plt.activeSubscriptions.push(subscription);
                                } else {
                                    if ($event) {
                                        $event.target.complete();
                                    }
                                }
                            }
                        } else {
                            this.loading = false;
                            this.dataLoading = false;
                        }
                    });
            } else {
                this.loading = false;
                this.dataLoading = false;
            }
        });
    }

    /**
     * aply the selected option
     * @param value - selected filter value
     */
    public applyFilter(value?: string) {

        this.userService.getCurrentUser().pipe(
            take(1),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {

                this.user = user;
                // always check for current event
                this.eventService.getCurrentEvent().pipe(
                    take(1),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        if (event.id) {
                            this.event = event;
                            if (sessionStorage.getItem('newsfeedFilter' + this.event.id)) {
                                this.filter = sessionStorage.getItem('newsfeedFilter' + this.event.id);
                            }

                            let previousValue = this.filter;
                            this.filter = value || this.filter;

                            if (this.filter == 'custom-keywords') {
                                if (value) {
                                    this.showKeywordsModal();
                                } else {
                                    if (sessionStorage.getItem('newsfeedfilterKeywords' + this.event.id)) {
                                        this.filterKeywords = JSON.parse(sessionStorage.getItem('newsfeedfilterKeywords' + this.event.id));
                                    }
                                    this.filterCustomKeywords = this.filterKeywords;
                                    this.refresh();
                                }
                            } else if (!value || previousValue !== this.filter) {
                                if (this.filter == 'my-keywords') {
                                    this.filterKeywords = [];
                                    this.user.selected_participant.keywords.forEach((item) => {
                                        this.filterKeywords.push({
                                            keyword_id: item.pivot.keyword_id || item.id,
                                            column: item.pivot.column
                                        });
                                    });
                                } else {
                                    this.filterKeywords = [];
                                    this.filterCustomKeywords = [];
                                }

                                this.refresh();
                            }

                            sessionStorage.setItem('newsfeedFilter' + this.event.id, this.filter);
                        } else {
                            this.loading = false;
                            this.dataLoading = false;
                        }
                    });
            } else {
                this.loading = false;
                this.dataLoading = false;
            }
        });
    }

    /**
     * open keyword select modal
     *
     * @return void
     */
    public async showKeywordsModal() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: KeywordSelectComponent,
            cssClass: 'keyword-select-modal',
            componentProps: {
                pivots: this.filterCustomKeywords
            }
        });

        modal.onWillDismiss().then((data) => {

            if (data.data?.keywords) {
                this.filterKeywords = this.filterCustomKeywords = data.data.keywords;
                sessionStorage.setItem('newsfeedfilterKeywords' + this.event.id, JSON.stringify(this.filterKeywords));
                this.refresh();
            }

            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

        });

        return await modal.present();
    }

    /**
     * show popover with filter
     * @return void
     */
    async showFilter(ev: any) {
        this.initFilterOptions();

        const popover = await this.popoverController.create({
            component: PopoverSelectBoxComponent,
            event: ev,
            componentProps: {
                'selected': this.filter,
                'options': this.filterOptions
            }
        });

        popover.onWillDismiss().then((data) => {
            if (data && data.data && data.data.selected) {
                this.applyFilter(data.data.selected);
            }
        });

        await popover.present();
    }

    /**
     * get selected filter option
     * @return SelectBoxOption
     */
    public getSelectedFilter() {
        let value = this.filter || 'all';

        let items = this.filterOptions.filter((item) => { return item.value == value });
        if (items.length) {
            return items[0];
        }

        return this.filterOptions.filter((item) => { return item.value == 'all' })[0];
    }

    /**
     * remove deleted newsfeed from post lists
     *
     * @param id
     *
     * @return void
     */
    public newsfeedDeleted(id) {
        // use output to refresh list
        this.items = this.items.filter((item) => { return item.id != id });
    }

    /**
     * init default filter options
     * add 'my-keywords' if particiapnt has some keywords and event has keywords
     * add 'custom-keywords' if event has keywords
     */
    public initFilterOptions() {
        this.filterOptions = [];

        // initiate filter
        this.filterOptions.push(new SelectBoxOption({ 'value': 'all', 'title': this.translate.instant('NEWSFEED_FILTER_ALL') }));
        this.filterOptions.push(new SelectBoxOption({ 'value': 'own', 'title': this.translate.instant('NEWSFEED_FILTER_OWN') }));

        if (this.showKeywords && this.user.selected_participant.keywords.length) {
            this.filterOptions.push(new SelectBoxOption({ 'value': 'my-keywords', 'title': this.translate.instant('NEWSFEED_FILTER_MY_KEYWORDS') }));
        }

        if (this.showKeywords) {
            this.filterOptions.push(new SelectBoxOption({ 'value': 'custom-keywords', 'title': this.translate.instant('NEWSFEED_FILTER_CUSTOM_KEYWORDS') }));
        }
    }

    resetFilter() {
        // sessionStorage.removeItem('newsfeedfilterKeywords' + this.event.id);
        // sessionStorage.removeItem('newsfeedFilter' + this.event.id);

        this.itemsOffset = 0;
        this.items = [];
        this.filter = 'all';
        this.filterKeywords = [];
        this.initFilterOptions();
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        if (this.eventChangeHandler) {
            this.appEvents.unsubscribe('event:changed', this.eventChangeHandler);
        }
        if (this.keywordsChangeHandler) {
            this.appEvents.unsubscribe('keywords:changed', this.keywordsChangeHandler);
        }
        if (this.refreshHandler) {
            this.appEvents.unsubscribe('newsfeed:refresh', this.refreshHandler);
        }

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
